/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { emitEvent, NoSSR } from "@cosmos/components";
import { pathnameHasLocale } from "@cosmos/common-utils";
import { isError } from "@epic-mw/error-types";
import { useAccountStore } from "@cosmos/next-hooks";
import {
	NavLinkClickEvent,
	NavCtaClickEvent,
	NavPropertyLogoClickEvent,
	MappedLocaleOptionFragment,
} from "@wf-components/navigation-consumer";
import { EpicGamesNavigation } from "@wf-components/navigation-react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import React, { useState, useEffect, useCallback } from "react";
import { locales } from "@i18n/config";
import { legalLocaleOptions } from "./locale-options";
import { useLocaleOptions } from "@site/localization/locale.hooks";

interface Props {
	locale: string;
}

function isNavLink(
	e: CustomEvent<NavLinkClickEvent | NavPropertyLogoClickEvent | NavCtaClickEvent>,
): e is CustomEvent<NavLinkClickEvent> {
	const event = e as CustomEvent<NavLinkClickEvent>;
	return Boolean(event.detail.link && event.detail.link?.label);
}

export const GlobalNavWrapper = ({ locale }: Props): React.JSX.Element | null => {
	const pathname = usePathname();
	const router = useRouter();
	const serverUrl = process.env.NEXT_PUBLIC_NAV_SERVER_URL || "https://www.epicgames.com";

	const [accountState, setAccountState] = useState("");
	const { account } = useAccountStore();

	useEffect(() => {
		if (window.location.href) {
			try {
				const url = new URL(window.location.href);
				if (url) {
					url.searchParams.append("state", pathname);
					url.searchParams.append("lang", locale.replace(/-/, "_"));
					setAccountState(url.searchParams.toString());
				}
			} catch (e) {
				console.log("Failed to parse href building accountState", window.location.href, isError(e) && e.message);
			}
		}
	}, [pathname, locale]);

	const navLinkOnClick = useCallback(
		(e: CustomEvent<NavLinkClickEvent | NavPropertyLogoClickEvent | NavCtaClickEvent>) => {
			const link = e.detail?.link;
			const navLinkEvent: CustomEvent<NavLinkClickEvent> | undefined = isNavLink(e) ? e : undefined;
			const label = navLinkEvent?.detail.link?.label || "";

			emitEvent({
				eventCategory: "headerNav",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: label || "property-logo",
				eventValue: {
					component: "GlobalNavigation",
					section: "navigation",
					key: link.__typename === "NavLink" ? link?.key : "",
					owner: link.owner,
					relativePath: link.relativePath,
				},
			});

			if (link && link?.owner === "mw-lh" && link.relativePath) {
				e.detail.originalEvent.preventDefault();
				let localeRelativePath = link.relativePath;
				if (!pathnameHasLocale(locales, localeRelativePath)) {
					localeRelativePath = `/${locale}${localeRelativePath}`;
				}
				router.push(localeRelativePath);
			}
		},
		[locale, router],
	);

	const ctaOnClick = useCallback((e: CustomEvent<NavCtaClickEvent>) => {
		const link = e.detail?.link;

		emitEvent({
			eventCategory: "headerNav",
			interactionType: "click",
			eventAction: "navigation",
			eventLabel: link.label || link.relativePath || link.hrefTemplate || "",
			eventValue: {
				component: "GlobalNavigation",
				section: "navigation",
				key: link.key,
				owner: link.owner,
				relativePath: link.relativePath,
			},
		});
	}, []);

	const localeOptions = useLocaleOptions();
	let finalLocaleOptions: MappedLocaleOptionFragment[] = legalLocaleOptions;
	if (pathname !== "/" && localeOptions && localeOptions.length) {
		finalLocaleOptions = localeOptions;
	}

	const searchParams = useSearchParams();
	const mode = searchParams.get("mode");
	if (mode === "game" || mode === "iosgame") {
		return null;
	}

	return (
		<NoSSR>
			<EpicGamesNavigation
				domain={serverUrl}
				locale={locale}
				isLoggedIn={account?.isLoggedIn}
				displayName={account?.displayName}
				propLogoOnClick={navLinkOnClick}
				navLinkOnClick={navLinkOnClick}
				disableSearch
				ctaOnClick={ctaOnClick}
				signInLinkHref={`/login${accountState ? `?${accountState}` : ""}`}
				signOutLinkHref={`/logout${accountState ? `?${accountState}` : ""}`}
				skipNavContentId="cosmos-app"
				localeOptions={finalLocaleOptions}
			/>
		</NoSSR>
	);
};
